import React, { useEffect, useMemo } from "react";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { Box, Button, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  TextInput,
  useListContext,
  SelectInput,
  ReferenceInput,
  AutocompleteInput,
} from "react-admin";

const ProductFilterForm = () => {
  const { displayedFilters, filterValues, setFilters, hideFilter } = useListContext();

  const form = useForm({
    defaultValues: {
      ...filterValues,
      q: filterValues.q || "",
      top_category_id: filterValues.top_category_id ?? null,
      child_category_id: filterValues.child_category_id ?? null,
      categoryId: filterValues.categoryId ?? null,
    },
  });

  const topCategoryId = useWatch({ control: form.control, name: "top_category_id" }) ?? null;
  const childCategoryId = useWatch({ control: form.control, name: "child_category_id" }) ?? null;

  useEffect(() => {
    const next = childCategoryId ?? topCategoryId ?? null;
    form.setValue("categoryId", next, { shouldDirty: true, shouldTouch: false });
  }, [childCategoryId, topCategoryId]);

  useEffect(() => {
    if (!displayedFilters.main) {
      form.reset();
    }
  }, [displayedFilters.main]);

  if (!displayedFilters.main) return null;

  const onSubmit = (values) => {
    const next = { ...values, categoryId: values.categoryId ?? null };
    delete next.top_category_id;
    delete next.child_category_id;

    if (Object.keys(next).length > 0) {
      setFilters(next);
    } else {
      hideFilter("main");
    }
  };

  const resetFilter = () => {
    form.reset();
    setFilters({}, []);
  };

  const CategoryFilters = () => {
    const sortBySortingAsc = useMemo(() => ({ field: "sorting", order: "ASC" }), []);
    const topFilter = useMemo(() => ({ level: "top" }), []);
    const childFilter = useMemo(
      () => (topCategoryId ? { parentId: topCategoryId } : { parentId: -1 }),
      [topCategoryId]
    );
    const equal = (o, v) => (o?.id ?? o) === (v?.id ?? v);

    return (
      <>
        {/* 父系列 */}
        <Box component="span" mr={2}>
          <ReferenceInput
            source="top_category_id"
            reference="product-categories"
            sort={sortBySortingAsc}
            filter={topFilter}
            perPage={200}
          >
            <AutocompleteInput
              label="父系列"
              optionText="label"
              optionValue="id"
              isOptionEqualToValue={equal}
              debounce={300}
              filterToQuery={(q) => ({ q })}
              helperText={false}
              sx={{ minWidth: "20rem" }}
              resettable
            />
          </ReferenceInput>
        </Box>

        {/* 子系列 */}
        <Box component="span" mr={2}>
          <ReferenceInput
            source="child_category_id"
            reference="product-categories"
            sort={sortBySortingAsc}
            filter={childFilter}
            perPage={500}
          >
            <AutocompleteInput
              label="子系列"
              optionText="label"
              optionValue="id"
              isOptionEqualToValue={equal}
              debounce={300}
              filterToQuery={(q) => ({ q })}
              helperText={false}
              sx={{ minWidth: "20rem" }}
              resettable
            />
          </ReferenceInput>
        </Box>

        {/* 隱藏欄位 */}
        <input type="hidden" {...form.register("categoryId")} />
      </>
    );
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box display="flex" alignItems="flex-end" mb={1}>
          <Box component="span" mr={2}>
            <TextInput
              resettable
              helperText={false}
              source="q"
              label="搜尋"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <CategoryFilters />

          <Box component="span" mr={2}>
            <SelectInput
              resettable
              source="status"
              label="上架狀態"
              emptyText="全部"
              helperText={false}
              choices={[
                { id: "true", name: "開啟" },
                { id: "false", name: "關閉" },
              ]}
            />
          </Box>

          <Box component="span" mr={2} mb={1.5}>
            <Button variant="outlined" color="primary" type="submit">
              篩選
            </Button>
          </Box>
          <Box component="span" mb={1.5}>
            <Button variant="outlined" onClick={resetFilter}>
              關閉
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default ProductFilterForm;
